import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldQuestion } from 'lucide-react';
import { Button, Notice } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { currentPosition } from '../../lib/location';
import { playChime } from '../../lib/sound';

const EVENT = 'tcrd:ride-check';
export const announceRideCheck = (rideId: string, body?: string) => window.dispatchEvent(new CustomEvent(EVENT, { detail: { rideId, body } }));

/**
 * "¿Todo bien?": aparece cuando el vehículo lleva mucho rato detenido o se aleja del destino.
 * "Necesito ayuda" activa la emergencia (aviso a la administración y a los contactos) y ofrece llamar al 911.
 */
export function RideCheckModal() {
  const qc = useQueryClient();
  const toast = useToast();
  const [check, setCheck] = useState<{ rideId: string; body?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const on = (e: Event) => {
      setCheck((e as CustomEvent<{ rideId: string; body?: string }>).detail);
      setHelp(false);
      playChime();
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  if (!check) return null;
  const answer = async (ok: boolean) => {
    setBusy(true);
    try {
      const pos = ok ? null : await currentPosition().catch(() => null);
      await api(`/rides/${check.rideId}/ridecheck`, { body: { ok, ...(pos ? { lat: pos.lat, lng: pos.lng } : {}) } });
      await qc.invalidateQueries({ queryKey: ['ride', check.rideId] });
      if (ok) {
        setCheck(null);
        toast('Gracias. Que tengas buen viaje.', 'success');
      } else setHelp(true);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="ridecheck-title" className="fixed inset-0 z-[3500] bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="w-full max-w-md rounded-3xl bg-[var(--surface)] p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] flex flex-col gap-4 text-center">
        <ShieldQuestion className="size-12 mx-auto text-[#2563eb]" aria-hidden />
        <h2 id="ridecheck-title" className="text-2xl font-bold">¿Todo bien?</h2>
        {help ? (
          <>
            <Notice tone="danger">Avisamos a la administración y a tus contactos de confianza con tu ubicación.</Notice>
            <a href="tel:911" className="tap h-14 rounded-2xl bg-brand-red text-white font-bold flex items-center justify-center">Llamar al 911</a>
            <Button variant="ghost" block onClick={() => setCheck(null)}>Cerrar</Button>
          </>
        ) : (
          <>
            <p className="text-[var(--text-muted)]">{check.body ?? 'Notamos algo inusual en tu viaje.'}</p>
            <Button size="lg" block loading={busy} onClick={() => void answer(true)}>Sí, todo bien</Button>
            <Button size="lg" block variant="accent" disabled={busy} onClick={() => void answer(false)}>Necesito ayuda</Button>
          </>
        )}
      </div>
    </div>
  );
}
